import { fetchMaintenanceStatus } from 'api/MaintenanceApiService';
import { subscribeToMaintenanceBlock } from './signal';
import { MaintenanceBlockSignal, MaintenanceNotice, MaintenanceStatus } from './types';

/**
 * Module-level cache of maintenance status, one entry per area, shared by every
 * gate on the page: any number of subscribers to an area cost one request per
 * refresh. Entries refresh every minute and when the window regains focus, and
 * immediately when an API call answers 503 MAINTENANCE.
 *
 * The status call fails open: a failed or unusable answer leaves `notices` null
 * (nothing is shown). A block learned from a 503 is kept as an `override` until
 * the next successful status answer, which is authoritative.
 */
export const REFRESH_INTERVAL_MS = 60_000;
/** Calls that are not forced (focus, mounts) are skipped if one started this recently. */
export const MIN_GAP_MS = 5_000;
/** Cache key of the site-wide (no area) entry. */
export const SITE_WIDE_KEY = '';

export interface MaintenanceEntry {
  /** Last usable answer, or null when never loaded or the last call failed. */
  notices: MaintenanceNotice[] | null;
  /** A block learned from a 503 MAINTENANCE answer. */
  override: MaintenanceNotice | null;
  /** True once the first call has settled (successfully or not). */
  loaded: boolean;
}

export const EMPTY_ENTRY: MaintenanceEntry = Object.freeze({
  notices: null,
  override: null,
  loaded: false,
});

let entries = new Map<string, MaintenanceEntry>();
let retained = new Map<string, number>();
let inflight = new Map<string, Promise<void>>();
let rerun = new Set<string>();
let lastStart = new Map<string, number>();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let lastSignalRefresh = 0;

const SIGNAL_REFRESH_GAP_MS = 2_000;

const emit = () => listeners.forEach((listener) => listener());

function setEntry(key: string, next: MaintenanceEntry): void {
  entries = new Map(entries).set(key, next);
  emit();
}

export const getEntry = (key: string): MaintenanceEntry => entries.get(key) ?? EMPTY_ENTRY;

export function subscribeToStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Refreshes one area. One request per area at a time: a call while one is in
 * flight shares it (a forced call queues exactly one follow-up), and unforced
 * calls within MIN_GAP_MS of the last start are skipped.
 */
export function refreshArea(key: string, force = false): Promise<void> {
  const running = inflight.get(key);
  if (running) {
    if (force) rerun.add(key);
    return running;
  }
  if (!force && Date.now() - (lastStart.get(key) ?? -Infinity) < MIN_GAP_MS) {
    return Promise.resolve();
  }
  lastStart.set(key, Date.now());

  const run = (async () => {
    let status: MaintenanceStatus | null = null;
    try {
      status = await fetchMaintenanceStatus(key === SITE_WIDE_KEY ? undefined : key);
    } catch {
      status = null;
    }
    const previous = getEntry(key);
    setEntry(
      key,
      status
        ? { notices: status.notices, override: null, loaded: true }
        : { notices: null, override: previous.override, loaded: true },
    );
  })().then(() => {
    inflight.delete(key);
    if (rerun.delete(key)) void refreshArea(key, true);
  });

  inflight.set(key, run);
  return run;
}

function refreshAll(force = false): void {
  retained.forEach((_count, key) => void refreshArea(key, force));
}

const onFocus = () => refreshAll();
const onVisible = () => {
  if (document.visibilityState === 'visible') refreshAll();
};

function startTimers(): void {
  if (timer !== null) return;
  timer = setInterval(() => refreshAll(), REFRESH_INTERVAL_MS);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisible);
}

function stopTimers(): void {
  if (timer === null) return;
  clearInterval(timer);
  timer = null;
  window.removeEventListener('focus', onFocus);
  document.removeEventListener('visibilitychange', onVisible);
}

/**
 * Registers interest in an area: loads it (shared with other subscribers) and
 * keeps it refreshing until the returned release function is called.
 */
export function retainArea(key: string): () => void {
  retained.set(key, (retained.get(key) ?? 0) + 1);
  startTimers();
  void refreshArea(key);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const count = (retained.get(key) ?? 1) - 1;
    if (count <= 0) retained.delete(key);
    else retained.set(key, count);
    if (retained.size === 0) stopTimers();
  };
}

function overrideFrom(signal: MaintenanceBlockSignal): MaintenanceNotice {
  return {
    state: 'active',
    mode: 'block',
    area: signal.area,
    impact: 'high',
    title: signal.title,
    message: signal.message,
    startsAt: new Date().toISOString(),
    endsAt: signal.endsAt,
  };
}

/** Applies a 503 MAINTENANCE answer: block the area now, then confirm with the server. */
export function applyBlockSignal(signal: MaintenanceBlockSignal): void {
  const override = overrideFrom(signal);
  const keys = signal.area === null ? [...entries.keys(), ...retained.keys()] : [signal.area];
  new Set(keys).forEach((key) => {
    const current = getEntry(key);
    setEntry(key, { ...current, override });
  });

  const now = Date.now();
  if (now - lastSignalRefresh >= SIGNAL_REFRESH_GAP_MS) {
    lastSignalRefresh = now;
    refreshAll(true);
  }
}

subscribeToMaintenanceBlock(applyBlockSignal);

/** Test helper: forgets every cached entry, timer and in-flight call. */
export function resetMaintenanceStore(): void {
  stopTimers();
  entries = new Map();
  retained = new Map();
  inflight = new Map();
  rerun = new Set();
  lastStart = new Map();
  lastSignalRefresh = 0;
}
